import {
  MESSAGE,
  createInitialDisplayState,
  createSignal,
  parseMessage,
  reduceDisplayState
} from './protocol';

export const HEARTBEAT_MS = 2000;
const BRANCH_STORAGE_KEY = 'permtang.customer-display.branch';

// Display-window side of the protocol (framework-independent). The Vue 3 view
// only renders `state`. Non-sensitive branch identity (name/logo) is cached in
// localStorage so idle/thank-you screens keep the logo after a refresh even if
// no publisher tab answers; customer/document data is never persisted.
export function createDisplayClient({ transport, win, onChange, heartbeatMs }) {
  const w = win || window;
  let state = createInitialDisplayState();
  let timer = null;
  let unsubscribe = null;

  try {
    const cached = JSON.parse(w.localStorage.getItem(BRANCH_STORAGE_KEY) || 'null');
    if (cached && typeof cached === 'object') state = { view: { kind: 'idle', branch: cached }, owner: null };
  } catch (error) {
    // No cache available.
  }

  function cacheBranch(branch) {
    if (!branch) return;
    try {
      w.localStorage.setItem(BRANCH_STORAGE_KEY, JSON.stringify(branch));
    } catch (error) {
      // Best effort only.
    }
  }

  function handle(raw) {
    const message = parseMessage(raw);
    if (!message) return;
    if (message.type === MESSAGE.FOCUS) {
      try {
        w.focus();
      } catch (error) {
        // Browsers may refuse to focus programmatically.
      }
      return;
    }
    if (message.type === MESSAGE.CLOSE) {
      // Only script-opened windows may close themselves; ours is opened that way.
      try {
        w.close();
      } catch (error) {
        // Ignore: the window stays open.
      }
      return;
    }
    if (message.type !== MESSAGE.STATE) return;
    const next = reduceDisplayState(state, message);
    if (next === state) return;
    state = next;
    cacheBranch(state.view.branch);
    onChange(state);
  }

  const onPageHide = () => transport.post(createSignal(MESSAGE.BYE));

  return {
    getState: () => state,
    // Tells the owning publisher tab a rating just completed (inline or
    // full-screen) - never the rating submission itself, which already went
    // straight to the backend. Purely a state-sync fact so a later HELLO
    // replay (this same window refreshed/reopened) does not resend stale,
    // already-rated controls.
    acknowledgeRatingCompleted(sessionId, rating) {
      if (sessionId) transport.post(createSignal(MESSAGE.RATING_COMPLETED, { sessionId, rating }));
    },
    // Tells the owning publisher tab the post-rating screen's own timer has
    // elapsed, so it can go fully idle too - otherwise a refresh/reopen would
    // replay the finished flow (and its settlement) from the start.
    acknowledgeFlowDone(sessionId) {
      if (sessionId) transport.post(createSignal(MESSAGE.RATING_FLOW_DONE, { sessionId }));
    },
    // Tells the owning publisher tab the matching pre-payment session is no
    // longer ratable (expired, or rejected as invalid) - the ACTIVE document
    // itself must stay visible, so this is deliberately not acknowledgeFlowDone.
    acknowledgeRatingUnavailable(sessionId) {
      if (sessionId) transport.post(createSignal(MESSAGE.RATING_UNAVAILABLE, { sessionId }));
    },
    start() {
      unsubscribe = transport.subscribe(handle);
      onChange(state);
      transport.post(createSignal(MESSAGE.HELLO));
      transport.post(createSignal(MESSAGE.ALIVE));
      timer = w.setInterval(() => transport.post(createSignal(MESSAGE.ALIVE)), heartbeatMs || HEARTBEAT_MS);
      w.addEventListener('pagehide', onPageHide);
    },
    stop() {
      if (timer) w.clearInterval(timer);
      if (unsubscribe) unsubscribe();
      w.removeEventListener('pagehide', onPageHide);
      onPageHide();
    }
  };
}
